import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Skeleton, Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BsPlusLg, BsTrash } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import Fiche from '../Expert/Fiche';
import InfoPopover from '../Pdf/listvariable';
import { themeExpert } from '../../theme/expert';

/** Les gabarits Word que l'on remplit avec les données d'un dossier. */
function OrganizationFiles() {
  const [gabarits, setGabarits] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [occupe, setOccupe] = useState(false);

  const [depot, setDepot] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axiosInstance.get('/orgafiles/');
      setGabarits(data.results || data);
      setErreur('');
    } catch {
      setErreur("Les gabarits n'ont pas pu être chargés.");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const deposer = async (e) => {
    e.preventDefault();
    setOccupe(true);
    try {
      const corps = new FormData();
      corps.append('title', depot.title.trim());
      corps.append('file', depot.fichier);
      await axiosInstance.post('/orgafiles/', corps);
      setDepot(null);
      setMessage('Gabarit ajouté');
      charger();
    } catch {
      setErreur("Le gabarit n'a pas pu être ajouté.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async () => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/orgafiles/${aSupprimer.id}/`);
      setASupprimer(null);
      setMessage('Gabarit supprimé');
      charger();
    } catch {
      setErreur("Le gabarit n'a pas pu être supprimé.");
    } finally {
      setOccupe(false);
    }
  };

  return (
    <>
      <Fiche
        titre="Gabarits de documents"
        explication="Un fichier Word avec des variables entre accolades. Depuis un dossier, il se remplit et se dépose dans ses pièces."
        actions={(
          <Stack direction="row" spacing={1.5}>
            <InfoPopover />
            <Button size="small" variant="contained" startIcon={<BsPlusLg />}
                    onClick={() => setDepot({ title: '', fichier: null })}>
              Ajouter
            </Button>
          </Stack>
        )}
        sx={{ p: 0 }}
      >
        {erreur && <Alert severity="error" sx={{ m: 2 }}
                          onClose={() => setErreur('')}>{erreur}</Alert>}

        {chargement && (
          <Stack spacing={1} sx={{ p: 2 }}>
            {[...Array(3)].map((_, i) => <Skeleton key={i} height={36} />)}
          </Stack>
        )}

        {!chargement && gabarits.length === 0 && (
          <Typography variant="body2" color="text.secondary"
                      sx={{ py: 4, textAlign: 'center' }}>
            Aucun gabarit pour l'instant.
          </Typography>
        )}

        {!chargement && gabarits.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Gabarit</TableCell>
                  <TableCell align="right">&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gabarits.map((gabarit) => (
                  <TableRow key={gabarit.id} hover>
                    <TableCell>{gabarit.title}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Supprimer">
                        <IconButton size="small"
                                    onClick={() => setASupprimer(gabarit)}>
                          <BsTrash />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Fiche>

      <ThemeProvider theme={themeExpert}>
        <Dialog open={depot !== null} onClose={() => setDepot(null)}
                fullWidth maxWidth="sm">
          <Box component="form" onSubmit={deposer} noValidate>
            <DialogTitle>Ajouter un gabarit</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <TextField label="Nom du gabarit" value={depot?.title || ''}
                           onChange={(e) => setDepot(
                             (s) => ({ ...s, title: e.target.value }))}
                           required autoFocus
                           helperText="C'est ce nom que vous verrez depuis un dossier." />
                <Box>
                  <Button component="label" variant="outlined">
                    {depot?.fichier ? 'Changer le fichier' : 'Choisir un fichier'}
                    <input type="file" hidden accept=".docx,.doc,.odt"
                           onChange={(e) => setDepot(
                             (s) => ({ ...s, fichier: e.target.files[0] }))} />
                  </Button>
                  <Typography variant="caption" color="text.secondary"
                              component="div" sx={{ mt: 1 }}>
                    {depot?.fichier ? depot.fichier.name
                      : 'Un document Word contenant les variables à remplir.'}
                  </Typography>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setDepot(null)} disabled={occupe}>
                Annuler
              </Button>
              <Button type="submit" variant="contained"
                      disabled={occupe || !depot?.title?.trim()
                                || !depot?.fichier}>
                {occupe ? 'Envoi…' : 'Ajouter'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        <Dialog open={aSupprimer !== null} onClose={() => setASupprimer(null)}>
          <DialogTitle>Supprimer ce gabarit ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {aSupprimer?.title} » ne sera plus proposé depuis les dossiers.
              Les documents déjà générés ne bougent pas.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimer} color="error" variant="contained"
                    disabled={occupe}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar open={Boolean(message)} autoHideDuration={3000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}

export default OrganizationFiles;
